import type { IEventService } from '@realitycollective/service-framework';
import { createServiceToken } from '@realitycollective/service-framework';
import type { WebRTCConnectionState, WebRTCEventMap } from '../webrtc/IWebRTCService';
import type { AssistRemoteConfig } from '../../protocol/assistRemoteConfig';
import type { ServerAvailabilityModel } from '../../core/serverAvailabilityModel';

/** A track kind on the room, transport-neutral. */
export type RoomTrackKind = 'video' | 'audio';

/** A remote participant's identity card (plain data - no SDK types). */
export interface RoomParticipant {
  /** Hub identity string (`wearer` / `vision` / `assistant:<slug>#<rand4>`). */
  readonly identity: string;
  /** Display name from the token (may be empty). */
  readonly name: string;
  /** Participant metadata JSON (e.g. `{"role":"vision"}`); empty when none. */
  readonly metadata: string;
}

/** One remote publication's state change, transport-neutral. */
export interface RoomTrackChange {
  readonly identity: string;
  readonly kind: RoomTrackKind;
  /** False = the publication was removed (unpublish). */
  readonly published: boolean;
  /** The publisher's own mute state (not the wearer's local hide/mute - that is subscription state). */
  readonly muted: boolean;
}

/** One data-topic message received on the room. */
export interface RoomDataMessage {
  readonly topic: string;
  readonly senderIdentity: string;
  readonly json: string;
}

/** A remote participant's video track became available (track) or went away (null). */
export interface RemoteVideoTrackChange {
  readonly identity: string;
  readonly track: MediaStreamTrack | null;
}

export type LiveKitRoomEventMap = WebRTCEventMap & {
  /** A remote participant joined the room (vision included - consumers filter by identity/metadata). */
  participantJoined: RoomParticipant;
  /** A remote participant left the room. */
  participantLeft: RoomParticipant;
  /** A remote publication appeared, disappeared, or changed its publisher-side mute. */
  remoteTrackChanged: RoomTrackChange;
  /** A participant's SFU connection quality changed: "poor" | "good" | "excellent" | "lost". */
  participantQuality: { readonly identity: string; readonly quality: string };
  /**
   * Discovery could not produce connectable credentials - config/token fetch
   * failed with no fallback. Carries the actionable sentence.
   */
  discoveryFailed: string;
  /** The served `/api/config` was fetched and parsed (RA-C10). */
  remoteConfig: AssistRemoteConfig;
  /** Every data-topic message, verbatim. */
  data: RoomDataMessage;
  /** Human-readable transport diagnostic line (status surfaces). */
  diagnostic: string;
  /** The human consented to frames leaving the device (`beginStreaming`). */
  streamingBegan: undefined;
  /** The D24 waiting-window model changed - surfaces re-render their card. */
  availabilityChanged: ServerAvailabilityModel;
  /** A remote video track subscribed (track set) or unsubscribed (track null). */
  remoteVideoTrack: RemoteVideoTrackChange;
};

/**
 * The LiveKit room transport - port of the Unity `ILiveKitRoomService`
 * (Plan-01 step 2). Everything `IWebRTCService` consumers already use
 * (`connectionState`, `detectionMessage`/`ready` events, `setVideoStream`),
 * plus the room-scoped surface: remote participant events, per-participant
 * subscription controls (D5's wearer hide/mute are LOCAL subscription
 * toggles), the consent-gated microphone, data topics, and the D24
 * availability model.
 *
 * Structurally a superset of `IWebRTCService`, so the same instance can be
 * handed to consumers of that seam (e.g. `DetectionService`).
 */
export interface ILiveKitRoomService extends IEventService<LiveKitRoomEventMap> {
  readonly connectionState: WebRTCConnectionState;

  /** The room connection is up (equivalent to `connectionState === 'connected'`). */
  readonly isRoomConnected: boolean;

  /** Snapshot of the remote participants currently in the room. */
  readonly remoteParticipants: readonly RoomParticipant[];

  /** Our own identity in the room (`wearer`); empty before connect. */
  readonly localIdentity: string;

  /** The connected room's name; empty before connect. */
  readonly roomName: string;

  /** Log-safe display of the endpoint in use (config endpoint or LiveKit URL) for status surfaces. */
  readonly activeEndpointDisplay: string;

  /** The last transport diagnostic line. */
  readonly lastDiagnostic: string;

  /**
   * The frames-consent gate: false until a human presses Enter on the warm-up
   * card. The video publish gate requires it (Unity `StreamingRequested`).
   */
  readonly streamingRequested: boolean;

  /** The human consented to frames leaving the device. Idempotent. */
  beginStreaming(): void;

  /**
   * Hand the transport the camera stream to publish. Publishing itself waits
   * for the full gate: room connected AND `streamingRequested` AND a live
   * video track on this stream.
   */
  setVideoStream(stream: MediaStream): void;

  /** Begin connecting (config/token/room). No-op while connecting/connected. Closes the D24 waiting window (a person pressed Join). */
  connect(): Promise<void>;

  /** Disconnect deliberately and suppress reconnect until `connect()`. */
  close(): void;

  /**
   * Subscribe/unsubscribe a participant's track kind for THIS client only
   * (D5: hide/mute are local subscription toggles; dynacast pauses upstream
   * publishes nobody subscribes to). False when the participant or a matching
   * publication is not present.
   */
  setTrackSubscribed(identity: string, kind: RoomTrackKind, subscribed: boolean): boolean;

  /**
   * Consent-gated microphone control (RA-C2/RA-C6). Enabling publishes the mic
   * track (first time, with echo cancellation) or unmutes it; disabling
   * hard-mutes immediately - the consent machine's pause must kill the mic at
   * once.
   */
  setMicrophoneEnabled(enabled: boolean): void;

  /** The mic track is published and not muted. */
  readonly microphoneEnabled: boolean;

  /** Publish one JSON payload on a room data topic. False when not connected. */
  publishData(topic: string, json: string, reliable?: boolean): boolean;

  /** Upstream training messages travel the `training` topic (Unity `SendDetectionMessage` parity). */
  sendDetectionMessage(json: string): boolean;

  /** The most recent served config; null before the first successful fetch. */
  readonly lastRemoteConfig: AssistRemoteConfig | null;

  /**
   * RA-C10's manual fallback: use this config endpoint (full URL or bare host
   * -> `https://<host>/api/config`) instead of the configured one, for the
   * rest of the session, and reconnect now. False when the input is unusable.
   */
  useConfigEndpoint(urlOrHost: string): boolean;

  /** Drop the manual override and return to the configured endpoint. */
  clearConfigEndpointOverride(): void;

  /**
   * The config endpoint actually in effect - a manual override when one is
   * set, otherwise the configured one. Empty before resolution. Exposed
   * because the console origin a wearer reads out has to be DERIVED from the
   * endpoint this session is really using.
   */
  readonly configEndpoint: string;

  /**
   * Plan-06 §9.6 / D24 - the waiting window: whether this cell is busy, when
   * the session holding it started, and whether Join may be offered.
   */
  readonly availability: ServerAvailabilityModel;
}

export const ILiveKitRoomService = createServiceToken<ILiveKitRoomService>('ILiveKitRoomService');
